import { useEffect, useState } from "react";

import ReportService from "src/Services/API/report.service";
import Loader from "src/Components/Common/Loader";
import { can, handleError } from "src/Common/function";

function Switch({ checked, mixed, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={mixed ? "mixed" : checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-[38px] h-[22px] rounded-full transition-colors duration-150 ${
        mixed ? "bg-[#F5C76A]" : checked ? "bg-[#0065FD]" : "bg-[#CBD5E1]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-150 ${
          mixed ? "translate-x-[8px]" : checked ? "translate-x-[16px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/**
 * Which hours system each person is on.
 *
 * Manual entry is the system as it has always worked: technicians type
 * regular, overtime and travel hours, and those figures are the figures.
 * Derived hours is the newer system: clock times, overtime worked out from
 * the person's rules, lunch, on call, and the weekly sign-off where a branch
 * has it on.
 *
 * The setting is the person's; the branch switch is a shortcut that moves
 * everyone in the branch at once. A technician in two branches moves with
 * either and is listed under both, which is the honest picture. Each flip
 * asks for a reason first, since it changes how a crew records hours, and
 * the reason goes to each person's hours history.
 */
const HoursSystemDrawer = ({ isOpen, onClose }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [draft, setDraft] = useState(null); // { kind: 'branch'|'user', id, name, derived, reason }
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const editable = can("time-management:setHoursSystem");

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return undefined;
    }
    let secondId;
    const firstId = requestAnimationFrame(() => {
      secondId = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(firstId);
      if (secondId) cancelAnimationFrame(secondId);
    };
  }, [isOpen]);

  const load = () => {
    setLoading(true);
    return ReportService.getHoursSystem()
      .then((response) => setBranches(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    setDraft(null);
    setNotice(null);
    load();
  }, [isOpen]);

  const confirm = () => {
    if (!draft?.reason.trim()) return;
    setSaving(true);
    const call =
      draft.kind === "branch"
        ? ReportService.setHoursSystemForBranch(draft.id, draft.derived, draft.reason.trim())
        : ReportService.setHoursSystemForUser(draft.id, draft.derived, draft.reason.trim());
    call
      .then((response) => {
        const row = response?.data || {};
        const to = draft.derived ? "derived hours" : "manual entry";
        setNotice(
          draft.kind === "branch"
            ? `${row.branchName ?? draft.name}: ${row.changed ?? 0} of ${row.total ?? "?"} moved to ${to}. Each was notified.`
            : `${row.name ?? draft.name} moved to ${to}${row.changed ? " and notified" : " (already there)"}.`
        );
        setDraft(null);
        return load();
      })
      .catch((error) => handleError(error))
      .finally(() => setSaving(false));
  };

  if (!isOpen && !visible) return null;

  const consequences = (derived, who) =>
    derived
      ? `${who} will enter start and stop times for work and travel from now on; overtime is worked out from the assigned rules, and lunch, on call and the weekly sign-off (where the branch has it on) apply. Hours already recorded stay as they are.`
      : `${who} will type regular, overtime and travel hours as before. Nothing is derived, no lunch or on call, no sign-off. Hours already recorded stay as they are; days recorded by clock time keep their regular and travel figures and show no overtime unless payroll sets it.`;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[560px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[18px] leading-6 font-OpenSansSemiBold text-[#1A2332]">Hours System</p>
            <p className="mt-1 text-[13px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              Manual entry: technicians type regular, overtime and travel hours, taken as entered.
              Derived hours: clock times, overtime from their rules, lunch, on call and sign-off. The
              setting is each person's; a branch switch moves everyone in it at once.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#FBFCFF] border-t border-[#E5E8F3] px-4 py-3">
          {notice ? (
            <div className="mb-2 rounded-[8px] border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-[12px] leading-[16px] font-OpenSansRegular text-[#166534]">
              {notice}
            </div>
          ) : null}
          {loading && branches.length === 0 ? (
            <div className="py-10 flex justify-center">
              <Loader variant="inline" ariaLabel="loading-hours-system" />
            </div>
          ) : branches.length === 0 ? (
            <p className="py-10 text-center text-[14px] font-OpenSansRegular text-[#868DA6]">No branches found.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {branches.map((branch) => {
                const allDerived = branch.total > 0 && branch.derived === branch.total;
                const mixed = branch.derived > 0 && branch.derived < branch.total;
                const editingBranch = draft?.kind === "branch" && draft.id === branch.branchId;
                const isOpenList = Boolean(expanded[branch.branchId]);
                return (
                  <div key={branch.branchId} className="bg-white rounded-[8px] border border-[#E5E8F3] px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setExpanded((prev) => ({ ...prev, [branch.branchId]: !prev[branch.branchId] }))}
                        className="min-w-0 text-left"
                      >
                        <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                          {branch.branchName || "Unnamed branch"}
                          <span className="ml-1.5 font-OpenSansRegular text-[12px] text-[#868DA6]">
                            {isOpenList ? "▾" : "▸"}
                          </span>
                        </p>
                        <p className="text-[12px] leading-[16px] font-OpenSansRegular text-[#868DA6]">
                          {branch.total === 0
                            ? "No technicians."
                            : allDerived
                              ? `All ${branch.total} on derived hours.`
                              : branch.derived === 0
                                ? `All ${branch.total} on manual entry.`
                                : `${branch.derived} of ${branch.total} on derived hours.`}
                        </p>
                      </button>
                      <Switch
                        label={`Derived hours for everyone at ${branch.branchName}`}
                        checked={editingBranch ? draft.derived : allDerived}
                        mixed={!editingBranch && mixed}
                        disabled={!editable || saving || branch.total === 0 || (draft && !editingBranch)}
                        onChange={(value) =>
                          editingBranch && value === allDerived
                            ? setDraft(null)
                            : setDraft({ kind: "branch", id: branch.branchId, name: branch.branchName, derived: value, reason: "" })
                        }
                      />
                    </div>

                    {editingBranch ? (
                      <ReasonBox
                        title={`Move everyone at ${branch.branchName} to ${draft.derived ? "derived hours" : "manual entry"}?`}
                        body={consequences(draft.derived, `All ${branch.total} technicians`)}
                        draft={draft}
                        setDraft={setDraft}
                        saving={saving}
                        onConfirm={confirm}
                      />
                    ) : null}

                    {isOpenList ? (
                      <div className="mt-2 border-t border-[#EEF0F9] pt-2 flex flex-col gap-1">
                        {branch.people.map((person) => {
                          const editingPerson = draft?.kind === "user" && draft.id === person.userId;
                          const elsewhere = person.branches.filter((name) => name !== branch.branchName);
                          return (
                            <div key={person.userId}>
                              <div className="flex items-center justify-between gap-3 py-1">
                                <div className="min-w-0">
                                  <p className="text-[13px] leading-[18px] font-OpenSansRegular text-[#1A2332] truncate">
                                    {person.name}
                                    <span className={`ml-2 text-[11px] ${person.hoursDerived ? "text-[#0065FD]" : "text-[#868DA6]"}`}>
                                      {person.hoursDerived ? "derived" : "manual"}
                                    </span>
                                  </p>
                                  {elsewhere.length ? (
                                    <p className="text-[11px] leading-[14px] font-OpenSansRegular text-[#A0A6BA]">
                                      Also in {elsewhere.join(", ")}
                                    </p>
                                  ) : null}
                                </div>
                                <Switch
                                  label={`Derived hours for ${person.name}`}
                                  checked={editingPerson ? draft.derived : person.hoursDerived}
                                  disabled={!editable || saving || (draft && !editingPerson)}
                                  onChange={(value) =>
                                    editingPerson && value === person.hoursDerived
                                      ? setDraft(null)
                                      : setDraft({ kind: "user", id: person.userId, name: person.name, derived: value, reason: "" })
                                  }
                                />
                              </div>
                              {editingPerson ? (
                                <ReasonBox
                                  title={`Move ${person.name} to ${draft.derived ? "derived hours" : "manual entry"}?`}
                                  body={consequences(draft.derived, person.name)}
                                  draft={draft}
                                  setDraft={setDraft}
                                  saving={saving}
                                  onConfirm={confirm}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E8F3] flex items-center justify-between gap-3">
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
            {editable ? "Each change asks for a reason before it is applied." : "You can view these settings but not change them."}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-[#E2E8F0] text-[#1F2A44] text-[14px] font-OpenSansSemiBold"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

const ReasonBox = ({ title, body, draft, setDraft, saving, onConfirm }) => (
  <div className="mt-2 rounded-[6px] border border-[#F5C76A] bg-[#FFF7E6] px-3 py-2.5">
    <p className="text-[12px] leading-[16px] font-OpenSansSemiBold text-[#92400E]">{title}</p>
    <p className="mt-1 text-[12px] leading-[16px] font-OpenSansRegular text-[#92400E]">{body}</p>
    <textarea
      value={draft.reason}
      onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
      placeholder="Why? Written to each person's hours history."
      rows={2}
      className="mt-2 w-full rounded-[6px] border border-[#E5E8F3] bg-white px-2.5 py-1.5 text-[13px] font-OpenSansRegular text-[#1A2332] outline-none focus:border-[#0065FD]"
    />
    <div className="mt-2 flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={() => setDraft(null)}
        disabled={saving}
        className="h-8 px-3 rounded-lg border border-[#E2E8F0] bg-white text-[#1F2A44] text-[13px] font-OpenSansSemiBold"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={saving || !draft.reason.trim()}
        className={`h-8 px-3 rounded-lg text-white text-[13px] font-OpenSansSemiBold ${
          saving || !draft.reason.trim() ? "bg-[#CAD5E2]" : "bg-[#0065FD] hover:bg-[#0052CC]"
        }`}
      >
        {saving ? "Saving…" : draft.derived ? "Move to derived" : "Move to manual"}
      </button>
    </div>
  </div>
);

export default HoursSystemDrawer;
